import { Circle, Group, Image as KonvaImage, Layer, Rect, Text } from "react-konva";
import { useEffect, useState } from "react";
import type { KonvaEventObject } from "konva/lib/Node";

import type { PlanogramItemOrientation } from "@CommonTypes/merchflow/pog/item";
import { Color } from "src/elements";
import { shouldRotateFacingImage } from "../../common/planogramFacing";
import { type OverlayPresentation, type OverlayPresentationToken } from "./overlays";
import type {
  PlanogramProductAsset,
  PlanogramProductCell,
  PlanogramOverlayBadgeRequest,
  PlanogramScene,
  PlanogramScenePlacement,
  PlanogramSceneProduct,
  PlanogramWorldRect,
} from "./types";

const getImageRotation = (orientation: PlanogramItemOrientation) =>
  shouldRotateFacingImage(orientation) ? 90 : 0;

const ProductImage = ({
  asset,
  cell,
}: {
  asset: PlanogramProductAsset;
  cell: PlanogramProductCell;
}) => {
  const rotation = getImageRotation(cell.orientation);
  const imageWidth = rotation === 0 ? cell.width : cell.height;
  const imageHeight = rotation === 0 ? cell.height : cell.width;

  return (
    <Group x={cell.x + cell.width / 2} y={cell.y + cell.height / 2} rotation={rotation}>
      <KonvaImage
        image={asset.image}
        height={imageHeight}
        width={imageWidth}
        x={-imageWidth / 2}
        y={-imageHeight / 2}
      />
    </Group>
  );
};

const getOverlayColor = (channel: string) => {
  if (channel === "search") return Color.pureYellow;
  if (channel === "business") return Color.paletteOrange;
  if (channel === "group") return Color.palettePurple;
  return Color.amber;
};

const hasOverlayToken = (
  presentation: OverlayPresentation,
  kind: OverlayPresentationToken["kind"],
) => presentation.tokens.some((token) => token.kind === kind);

const getSafeScale = (scale: number) => (Number.isFinite(scale) && scale > 0 ? scale : 1);

const STATUS_DOT_DIAMETER_PX = 10;
const STATUS_DOT_RING_THICKNESS_PX = 2;
const STATUS_DOT_OUTER_DIAMETER_PX = STATUS_DOT_DIAMETER_PX + STATUS_DOT_RING_THICKNESS_PX * 2;

const StatusDot = ({
  color,
  name,
  scale = 1,
  x,
  y,
}: {
  color: string;
  name: string;
  scale?: number;
  x: number;
  y: number;
}) => {
  const px = (screenPixels: number) => screenPixels / getSafeScale(scale);
  const outerRadius = px(STATUS_DOT_OUTER_DIAMETER_PX / 2);

  return (
    <Group listening={false} name={name} x={x + outerRadius} y={y + outerRadius}>
      <Circle
        fill={Color.white}
        listening={false}
        name={`${name}-ring`}
        radius={outerRadius}
        shadowBlur={px(2)}
        shadowColor="rgba(0, 0, 0, 0.25)"
        shadowOpacity={1}
      />
      <Circle
        fill={color}
        listening={false}
        name={`${name}-fill`}
        radius={px(STATUS_DOT_DIAMETER_PX / 2)}
      />
    </Group>
  );
};

const ProductVisualOverlays = ({
  displayMode,
  labelFontSize,
  product,
  scale,
  showLabel,
  textFill,
}: {
  displayMode: "image" | "label";
  labelFontSize: number;
  product: PlanogramSceneProduct;
  scale: number;
  showLabel: boolean;
  textFill: string;
}) => {
  const shouldRenderLabel = showLabel && displayMode === "label";
  if (!shouldRenderLabel && !product.isNewInsertedItem) return null;

  return (
    <>
      {shouldRenderLabel && (
        <Text
          align="center"
          fill={textFill}
          fontSize={labelFontSize}
          height={product.height}
          listening={false}
          name={`product-visual-label-${product.id}`}
          text={product.label}
          verticalAlign="middle"
          width={product.width}
        />
      )}
      {product.isNewInsertedItem &&
        product.cells.map((cell, index) => (
          <StatusDot
            color={Color.primary}
            key={`${product.id}:new-item:${cell.kind}:${index}`}
            name={`new-item-indicator-${product.id}-${index}`}
            scale={scale}
            x={cell.x}
            y={cell.y}
          />
        ))}
    </>
  );
};

export interface OverlayEntity extends PlanogramWorldRect {
  readonly id: string;
}

export const isWorldRectVisible = (
  rect: PlanogramWorldRect,
  viewport: { offsetX: number; offsetY: number; scale: number },
  viewportWidth: number,
  viewportHeight: number,
) => {
  const left = -viewport.offsetX / viewport.scale;
  const top = -viewport.offsetY / viewport.scale;
  const right = (viewportWidth - viewport.offsetX) / viewport.scale;
  const bottom = (viewportHeight - viewport.offsetY) / viewport.scale;
  return (
    rect.x < right && rect.x + rect.width > left && rect.y < bottom && rect.y + rect.height > top
  );
};

export const getOverlayEntities = (scene: PlanogramScene): OverlayEntity[] => [
  ...scene.bays,
  ...scene.containers,
  ...scene.shelves,
  ...scene.fixtures,
  ...(scene.pegboards ?? []),
  ...(scene.placements ?? []),
  ...scene.products,
];

/**
 * Overlay strokes, dashes, and badges are authored in SCREEN pixels and
 * divided by the viewport scale, because the Stage carries the scale for
 * every layer. Authored in world units they collapse to a fraction of a
 * pixel at planogram fit-scale (a real pog fits at roughly 0.02), which is
 * how canvas highlights became invisible while the DOM renderer's were not.
 */
const OVERLAY_BADGE_HEIGHT_PX = 14;
const OVERLAY_BADGE_CHARACTER_PX = 7;
const OVERLAY_BADGE_INSET_PX = 1;
const OVERLAY_BADGE_FONT_PX = 10;
const OVERLAY_BADGE_FILL = "#D64545";
const OVERLAY_PULSE_INTERVAL_MS = 600;

export const PresentationOverlays = ({
  entities,
  onBadgeActivate,
  presentations,
  scale = 1,
}: {
  entities: readonly OverlayEntity[];
  onBadgeActivate?: (request: PlanogramOverlayBadgeRequest) => void;
  presentations: readonly OverlayPresentation[];
  /** Active viewport scale; overlay line work is kept at constant screen size. */
  scale?: number;
}) => {
  const entitiesById = new Map(entities.map((entity) => [entity.id, entity]));
  const px = (screenPixels: number) => screenPixels / getSafeScale(scale);
  const hasPulsingPresentation = presentations.some((presentation) =>
    hasOverlayToken(presentation, "pulse"),
  );
  const [pulseDimmed, setPulseDimmed] = useState(false);
  const [hoveredBadgeId, setHoveredBadgeId] = useState<string | null>(null);

  useEffect(() => {
    if (!hasPulsingPresentation) {
      setPulseDimmed(false);
      return;
    }

    const interval = window.setInterval(
      () => setPulseDimmed((current) => !current),
      OVERLAY_PULSE_INTERVAL_MS,
    );
    return () => window.clearInterval(interval);
  }, [hasPulsingPresentation]);

  return (
    <Layer listening={onBadgeActivate !== undefined} name="planogram-overlays">
      {presentations.map((presentation) => {
        const entity = entitiesById.get(presentation.id);
        if (!entity) return null;
        const emphasisColor =
          presentation.emphasis.channel === null
            ? Color.transparent
            : (presentation.emphasis.color ?? getOverlayColor(presentation.emphasis.channel));
        const isSearchResult = presentation.emphasis.channel === "search";
        const isPulsing = hasOverlayToken(presentation, "pulse");
        const badge = presentation.badge;
        const badgeWidth = px(
          Math.max(OVERLAY_BADGE_HEIGHT_PX, (badge?.length ?? 0) * OVERLAY_BADGE_CHARACTER_PX + 6),
        );

        return (
          <Group
            key={presentation.id}
            listening={badge !== null && onBadgeActivate !== undefined}
            name={`overlay-${presentation.id}`}
          >
            {presentation.emphasis.state === "de-emphasized" && (
              <Rect
                fill={Color.white}
                height={entity.height}
                listening={false}
                opacity={0.62}
                width={entity.width}
                x={entity.x}
                y={entity.y}
              />
            )}
            {hasOverlayToken(presentation, "hover") && (
              <Rect
                dash={[px(4), px(3)]}
                height={entity.height}
                listening={false}
                name={`overlay-hover-${presentation.id}`}
                stroke={Color.spaceGrayHover}
                strokeWidth={px(2)}
                width={entity.width}
                x={entity.x}
                y={entity.y}
              />
            )}
            {hasOverlayToken(presentation, "highlight") && (
              <>
                {/* DOM parity: Item.tsx paints a translucent wash over the whole
                    item, so the highlight reads at any zoom — an outline alone
                    disappears among neighbouring products. */}
                <Rect
                  fill={emphasisColor}
                  height={entity.height}
                  listening={false}
                  name={`overlay-highlight-fill-${presentation.id}`}
                  opacity={isPulsing && pulseDimmed ? 0.18 : isSearchResult ? 0.55 : 0.35}
                  width={entity.width}
                  x={entity.x}
                  y={entity.y}
                />
                <Rect
                  dash={isSearchResult ? undefined : [px(8), px(4)]}
                  height={entity.height}
                  listening={false}
                  name={`overlay-highlight-${presentation.id}`}
                  opacity={isPulsing && pulseDimmed ? 0.35 : 1}
                  stroke={isSearchResult ? Color.black : emphasisColor}
                  strokeWidth={px(hasOverlayToken(presentation, "pulse") ? 4 : 3)}
                  width={entity.width}
                  x={entity.x}
                  y={entity.y}
                />
              </>
            )}
            {hasOverlayToken(presentation, "selection") && (
              <Rect
                height={entity.height}
                listening={false}
                name={`overlay-selection-${presentation.id}`}
                stroke={Color.primary}
                strokeWidth={px(2)}
                width={entity.width}
                x={entity.x}
                y={entity.y}
              />
            )}
            {hasOverlayToken(presentation, "focus") && (
              <Rect
                dash={[px(5), px(3)]}
                height={entity.height}
                listening={false}
                name={`overlay-focus-${presentation.id}`}
                stroke={Color.infoBlueDark}
                strokeWidth={px(2)}
                width={entity.width}
                x={entity.x}
                y={entity.y}
              />
            )}
            {badge !== null && (
              <Group
                listening={onBadgeActivate !== undefined}
                name={`overlay-badge-${presentation.id}`}
                onClick={(event: KonvaEventObject<MouseEvent>) => {
                  event.cancelBubble = true;
                }}
                onMouseDown={(event: KonvaEventObject<MouseEvent>) => {
                  event.cancelBubble = true;
                }}
                onMouseEnter={(event: KonvaEventObject<MouseEvent>) => {
                  event.cancelBubble = true;
                  setHoveredBadgeId(presentation.id);
                  const stage = event.target.getStage();
                  if (stage) stage.container().style.cursor = "pointer";
                  onBadgeActivate?.({
                    entityId: presentation.id,
                    position: { clientX: event.evt.clientX, clientY: event.evt.clientY },
                  });
                }}
                onMouseLeave={(event: KonvaEventObject<MouseEvent>) => {
                  event.cancelBubble = true;
                  setHoveredBadgeId((current) => (current === presentation.id ? null : current));
                  const stage = event.target.getStage();
                  if (stage) stage.container().style.cursor = "default";
                }}
                onTap={(event: KonvaEventObject<TouchEvent>) => {
                  event.cancelBubble = true;
                  const touch = event.evt.changedTouches[0];
                  if (!touch) return;
                  onBadgeActivate?.({
                    entityId: presentation.id,
                    position: { clientX: touch.clientX, clientY: touch.clientY },
                  });
                }}
                onTouchStart={(event: KonvaEventObject<TouchEvent>) => {
                  event.cancelBubble = true;
                }}
              >
                <Rect
                  cornerRadius={px(OVERLAY_BADGE_HEIGHT_PX / 2)}
                  fill={hoveredBadgeId === presentation.id ? "#E45C5C" : OVERLAY_BADGE_FILL}
                  height={px(OVERLAY_BADGE_HEIGHT_PX)}
                  listening={onBadgeActivate !== undefined}
                  shadowBlur={hoveredBadgeId === presentation.id ? px(5) : 0}
                  shadowColor="rgba(0, 0, 0, 0.45)"
                  shadowOpacity={hoveredBadgeId === presentation.id ? 1 : 0}
                  stroke={Color.white}
                  strokeWidth={px(1)}
                  width={badgeWidth}
                  x={entity.x + entity.width - badgeWidth - px(OVERLAY_BADGE_INSET_PX)}
                  y={entity.y + px(OVERLAY_BADGE_INSET_PX)}
                />
                <Text
                  align="center"
                  fill={Color.white}
                  fontSize={px(OVERLAY_BADGE_FONT_PX)}
                  fontStyle="bold"
                  height={px(OVERLAY_BADGE_HEIGHT_PX)}
                  listening={false}
                  name={`overlay-badge-text-${presentation.id}`}
                  text={badge}
                  verticalAlign="middle"
                  width={badgeWidth}
                  x={entity.x + entity.width - badgeWidth - px(OVERLAY_BADGE_INSET_PX)}
                  y={entity.y + px(OVERLAY_BADGE_INSET_PX)}
                />
              </Group>
            )}
          </Group>
        );
      })}
    </Layer>
  );
};

export const PlacementCells = ({
  asset,
  color,
  displayMode,
  labelFontSize = 16,
  placement,
  scale,
  showLabel,
}: {
  asset: PlanogramProductAsset | undefined;
  color?: string;
  displayMode: "image" | "label";
  labelFontSize?: number;
  placement: PlanogramScenePlacement;
  scale: number;
  showLabel: boolean;
}) => {
  const imageAvailable = displayMode === "image" && asset !== undefined;
  const labelColor = displayMode === "label" ? color : undefined;
  const placementFill = labelColor ?? Color.purple100;
  const textFill = labelColor ? Color.white : Color.textMain;

  return (
    <>
      {placement.facingCells.map((cell, index) => (
        <Group
          key={`${placement.id}:facing:${cell.holeNoLeft}:${cell.holeNoTop}:${cell.depth}:${index}`}
          name={`pegboard-facing-${placement.id}-${index}`}
          x={cell.x}
          y={cell.y}
        >
          <Rect
            fill={placementFill}
            height={cell.height}
            name={`pegboard-facing-cell-${placement.id}-${index}`}
            stroke={Color.spaceGray}
            strokeWidth={1}
            width={cell.width}
          />
          {imageAvailable ? (
            <KonvaImage image={asset.image} height={cell.height} width={cell.width} />
          ) : showLabel && displayMode === "label" ? (
            <Text
              align="center"
              fill={textFill}
              fontSize={labelFontSize}
              height={cell.height}
              listening={false}
              text={placement.label}
              verticalAlign="middle"
              width={cell.width}
            />
          ) : null}
          {placement.facingChangeColor && (
            <StatusDot
              color={placement.facingChangeColor}
              name={`pegboard-facing-change-${placement.id}-${index}`}
              scale={scale}
              x={0}
              y={0}
            />
          )}
        </Group>
      ))}
    </>
  );
};

export const ProductCells = ({
  asset,
  color,
  detail,
  labelFontSize = 16,
  product,
  scale = 1,
  showLabel,
  displayMode,
}: {
  asset: PlanogramProductAsset | undefined;
  color?: string;
  displayMode: "image" | "label";
  detail: "full" | "reduced";
  labelFontSize?: number;
  product: PlanogramSceneProduct;
  scale?: number;
  showLabel: boolean;
}) => {
  const imageAvailable = displayMode === "image" && asset !== undefined;
  const labelColor = displayMode === "label" ? color : undefined;
  const productFill = labelColor ?? Color.purple100;
  const textFill = labelColor ? Color.white : Color.textMain;

  if (detail === "reduced") {
    return (
      <>
        <Rect
          fill={productFill}
          height={product.height}
          name="product-detail-reduced"
          stroke={Color.spaceGray}
          strokeWidth={1}
          width={product.width}
        />
        <ProductVisualOverlays
          displayMode={displayMode}
          labelFontSize={labelFontSize}
          product={product}
          scale={scale}
          showLabel={showLabel}
          textFill={textFill}
        />
      </>
    );
  }

  return (
    <>
      {product.cells.map((cell, index) => (
        <Group key={`${product.id}:${cell.kind}:${index}`}>
          <Rect
            fill={productFill}
            height={cell.height}
            name={`product-cell-${cell.kind}`}
            stroke={Color.spaceGray}
            strokeWidth={1}
            width={cell.width}
            x={cell.x}
            y={cell.y}
          />
          {imageAvailable && <ProductImage asset={asset} cell={cell} />}
        </Group>
      ))}
      <ProductVisualOverlays
        displayMode={displayMode}
        labelFontSize={labelFontSize}
        product={product}
        scale={scale}
        showLabel={showLabel}
        textFill={textFill}
      />
    </>
  );
};
